// TelcoTrack — parsování Tabulka_PPD.xlsx (první list) z matice buněk

import type { PpdParseResult, PpdParsedLine, PpdParsedMetadata } from '../constants/ppd-report.constants';

function cellStr(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

/** Odstraní mezery mezi písmeny („S e z n a m p o l o ž e k“) a diakritiku pro porovnání hlaviček. */
export function normalizePpdHeaderText(raw: string): string {
  return cellStr(raw)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
}

function isSapValue(v: unknown): boolean {
  if (typeof v === 'number' && Number.isFinite(v)) return v >= 10000;
  const s = cellStr(v).replace(/\s/g, '');
  return /^\d{5,7}$/.test(s);
}

function normalizeSap(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  return cellStr(v).replace(/\s/g, '');
}

/** Parsuje množství ve tvaru „8.00 m“, „153423.00 JV“, „50.00 m2“. */
export function parsePpdQuantityText(raw: string): { quantity: number; unit: string } | null {
  const s = cellStr(raw).replace(/\u00a0/g, ' ');
  const m = s.match(/^([\d.,]+)\s*(.*)$/);
  if (!m) return null;
  const qty = Number.parseFloat(m[1].replace(',', '.'));
  if (!Number.isFinite(qty)) return null;
  return { quantity: qty, unit: m[2].trim() };
}

function isCategoryRow(row: unknown[]): boolean {
  const a = cellStr(row[0]);
  if (!a || isSapValue(row[0])) return false;
  const b = cellStr(row[1]);
  const c = cellStr(row[2]);
  if (b || c) return false;
  if (a.includes(':')) return false;
  return a.length > 2;
}

export function isPpdSapHeaderRow(row: unknown[]): boolean {
  const cells = (row ?? []).map(cellStr);
  for (const cell of cells) {
    const n = normalizePpdHeaderText(cell);
    if (!n) continue;
    if (n.includes('cislosap') || (n.includes('cislo') && n.includes('sap'))) return true;
  }
  const joined = normalizePpdHeaderText(cells.join(' '));
  return joined.includes('cislosap');
}

export function isPpdSectionTitleRow(row: unknown[]): boolean {
  const joined = normalizePpdHeaderText((row ?? []).map(cellStr).join(' '));
  if (!joined) return false;
  if (joined.includes('seznam') && joined.includes('poloz')) return true;
  if (joined.includes('limitka') && joined.includes('material')) return true;
  return false;
}

export function findAllPpdSapHeaderRows(matrix: unknown[][]): number[] {
  const rows: number[] = [];
  for (let i = 0; i < matrix.length; i++) {
    if (isPpdSapHeaderRow(matrix[i] ?? [])) rows.push(i);
  }
  return rows;
}

export function findPpdItemsSectionStart(matrix: unknown[][]): number {
  const sapHeaders = findAllPpdSapHeaderRows(matrix);
  if (sapHeaders.length > 0) return sapHeaders[0];

  for (let i = 0; i < matrix.length; i++) {
    if (!isPpdSectionTitleRow(matrix[i] ?? [])) continue;
    return i + 1;
  }
  return -1;
}

function hasPpdItemsSection(matrix: unknown[][]): boolean {
  if (findAllPpdSapHeaderRows(matrix).length > 0) return true;
  for (let i = 0; i < matrix.length; i++) {
    if (isPpdSectionTitleRow(matrix[i] ?? [])) return true;
  }
  return false;
}

function hasPpdTitleRow(matrix: unknown[][]): boolean {
  for (let i = 0; i < Math.min(matrix.length, 8); i++) {
    const joined = (matrix[i] ?? []).map(cellStr).join(' ').toLowerCase();
    if (!joined.includes('ppd')) continue;
    if (joined.includes('propočet') || joined.includes('propocet') || joined.includes('projekt')) {
      return true;
    }
  }
  return false;
}

function findQuantityColumnIndex(headerRow: unknown[]): number {
  const row = headerRow ?? [];
  for (let c = 0; c < row.length; c++) {
    const n = normalizePpdHeaderText(cellStr(row[c]));
    if (n.includes('mnozstvi')) return c;
  }
  return 5;
}

function findTotalPriceColumnIndex(headerRow: unknown[]): number {
  const row = headerRow ?? [];
  for (let c = 0; c < row.length; c++) {
    const n = normalizePpdHeaderText(cellStr(row[c]));
    if (n.includes('celkova') && n.includes('cena')) return c;
  }
  return row.length > 6 ? 6 : Math.max(0, row.length - 1);
}

function isQuantityUnit(unit: string): boolean {
  const u = unit
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return u.length > 0 && u !== 'kc';
}

/** Parsuje částku ve tvaru „30 000,00 Kč“, „677.248,00 Kč“, „30,000.00 Kč“ (Excel) nebo numerickou hodnotu. */
export function parsePpdMoneyText(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = cellStr(raw).replace(/[\u00a0\u202f\u2007\u2009]/g, ' ').trim();
  if (!s) return null;

  let cleaned = s.replace(/kč/gi, '').trim();
  // „6392.00 m“ / „90321.00 JV“ je množství, ne částka. parseFloat by z toho vzal jen číslo.
  if (/[a-z]/i.test(cleaned)) return null;

  const compact = cleaned.replace(/\s/g, '');

  // Excel / US: 30,000.00 nebo 101,422.00
  if (/^[\d,]+\.\d+$/.test(compact) && compact.includes(',')) {
    const n = Number.parseFloat(compact.replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  // České .xls: 677.248,00 nebo 3.523.166,87 (tečka = tisíce, čárka = desetiny).
  // parseFloat by z „677.248,00“ vzal jen 677 a řádek by spadl do ceníku.
  if (/^\d{1,3}(\.\d{3})+,\d+$/.test(compact)) {
    const n = Number.parseFloat(compact.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  // České: 30 000,00 nebo 3 004 421,80
  cleaned = compact.replace(',', '.');
  if (!/^[-+]?\d+(\.\d+)?$/.test(cleaned)) return null;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

function cellMoney(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return parsePpdMoneyText(v);
}

function findQuantityInRow(row: unknown[], qtyCol: number): { quantity: number; unit: string } | null {
  const cols: number[] = [];
  for (let d = -1; d <= 3; d++) cols.push(qtyCol + d);
  for (let c = 1; c < row.length; c++) cols.push(c);

  const seen = new Set<number>();
  for (const c of cols) {
    if (c < 1 || c >= row.length || seen.has(c)) continue;
    seen.add(c);
    const parsed = parsePpdQuantityText(cellStr(row[c]));
    if (parsed && isQuantityUnit(parsed.unit)) return parsed;
  }
  return null;
}

function looksLikeMoneyCell(v: unknown): boolean {
  if (typeof v === 'number' && Number.isFinite(v)) return true;
  const raw = cellStr(v);
  return /kč|kc/i.test(raw) || /^[\d\s.,]+,\d{3}\.\d{2}$/.test(raw.replace(/\s/g, ''));
}

function findLineTotalInRow(row: unknown[], totalCol: number): number | null {
  // Explicitní „Kč“ je Celková cena, i 0 Kč a i když leží mimo odhadnutý sloupec.
  for (let c = row.length - 1; c >= 0; c--) {
    const raw = cellStr(row[c]);
    if (!/kč/i.test(raw)) continue;
    const price = parsePpdMoneyText(row[c]);
    if (price != null) return price;
  }

  const qty = findQuantityInRow(row, totalCol)?.quantity ?? null;
  const candidates: number[] = [];
  for (let c = totalCol; c < row.length; c++) candidates.push(c);
  for (let c = row.length - 1; c >= Math.max(0, row.length - 4); c--) candidates.push(c);
  if (row.length > 0) candidates.push(row.length - 1);

  const seen = new Set<number>();
  for (const c of candidates) {
    if (c < 0 || c >= row.length || seen.has(c)) continue;
    seen.add(c);
    const cell = row[c];
    if (cell == null || cellStr(cell) === '') continue;
    const asQty = parsePpdQuantityText(cellStr(cell));
    if (asQty && isQuantityUnit(asQty.unit)) continue;
    if (!looksLikeMoneyCell(cell)) continue;
    const price = cellMoney(cell);
    if (price == null) continue;
    // Holé množství není Celková cena (592 místo 677 248). 0 Kč má v textu „Kč“ a vrátí se výš.
    if (qty != null && Math.abs(price - qty) < 0.001) continue;
    return price;
  }
  return null;
}

function rowMatchesSap(row: unknown[], sap: string): boolean {
  for (let c = 0; c <= 3; c++) {
    if (isSapValue(row[c]) && normalizeSap(row[c]) === sap) return true;
  }
  return false;
}

/**
 * Doplní ppdLineTotal z ostatních variant parsování nebo přímým scanem buněk řádku SAP.
 * .xls exporty často vrátí více řádků v jedné variantě, ale ceny až v jiné (raw vs formatted).
 */
export function enrichPpdParseLineTotals(
  lines: PpdParsedLine[],
  parseResults: PpdParseResult[],
  matrices: unknown[][],
): void {
  const bySap = new Map<string, number>();
  for (const result of parseResults) {
    for (const line of result.lines) {
      if (line.ppdLineTotal != null && line.ppdLineTotal > 0) {
        bySap.set(line.sap, line.ppdLineTotal);
      }
    }
  }

  for (const line of lines) {
    // 0 Kč je cena z dokumentu (nesmí ji přepsat množství z jiné varianty).
    if (line.ppdLineTotal != null) continue;
    const merged = bySap.get(line.sap);
    if (merged != null && merged > 0) {
      line.ppdLineTotal = merged;
    }
  }

  for (const line of lines) {
    if (line.ppdLineTotal != null) continue;
    outer: for (const matrix of matrices) {
      for (const row of matrix) {
        const cells = row ?? [];
        if (!Array.isArray(cells) || !rowMatchesSap(cells, line.sap)) continue;
        for (const totalCol of [6, 5, 4, Math.max(0, cells.length - 1)]) {
          const total = findLineTotalInRow(cells, totalCol);
          if (total != null && total > 0) {
            line.ppdLineTotal = total;
            break outer;
          }
        }
      }
    }
  }
}

/** Vrátí českou chybovou hlášku, nebo null pokud je soubor platné Tabulka_PPD. */
export function validatePpdMatrix(matrix: unknown[][], parsed?: PpdParseResult): string | null {
  const result = parsed ?? parsePpdMatrix(matrix);

  if (!hasPpdTitleRow(matrix)) {
    return (
      'Soubor není export Tabulka_PPD — chybí záhlaví „PPD - Propočet projektové dokumentace“. ' +
      'Nahrajte soubor Tabulka_PPD.xlsx, ne jiný Excel (WF, akvizice, věcná břemena, …).'
    );
  }

  if (!result.metadata.buildName?.trim()) {
    return (
      'Soubor není ve formátu Tabulka_PPD — chybí pole „Název stavby“ v hlavičce. ' +
      'Zkontrolujte, že nahráváte správný export PPD.'
    );
  }

  if (!hasPpdItemsSection(matrix)) {
    return (
      'Soubor není ve formátu Tabulka_PPD — chybí sekce „Seznam položek“ a hlavička sloupce „Číslo SAP“.'
    );
  }

  const parseErr = validatePpdParseResult(result);
  if (parseErr) return parseErr;

  return null;
}

/** Validace parsovaných dat (backend — bez surové matice). */
export function validatePpdParseResult(parsed: PpdParseResult): string | null {
  if (!parsed.metadata.buildName?.trim()) {
    return (
      'Soubor není ve formátu Tabulka_PPD — chybí pole „Název stavby“ v hlavičce. ' +
      'Zkontrolujte, že nahráváte správný export PPD.'
    );
  }

  if (!parsed.metadata.sppNumber?.trim() && !parsed.metadata.catalogLabel?.trim()) {
    return (
      'Soubor není ve formátu Tabulka_PPD — chybí metadata SPP / ceník v hlavičce. ' +
      'Pravděpodobně jste nahráli jiný Excel.'
    );
  }

  if (!Array.isArray(parsed.lines) || parsed.lines.length === 0) {
    return 'Soubor neobsahuje žádné položky PPD k importu.';
  }

  const withUnit = parsed.lines.filter((l) => l.unit.trim().length > 0).length;
  if (withUnit === 0) {
    return (
      'Soubor není ve formátu Tabulka_PPD — nalezené řádky nemají množství s jednotkou (m, ks, JV, …). ' +
      'Pravděpodobně jste nahráli jiný Excel.'
    );
  }

  if (withUnit / parsed.lines.length < 0.5) {
    return (
      'Soubor pravděpodobně není Tabulka_PPD — většina řádků nemá platné množství. ' +
      'Nahrajte export PPD ze systému.'
    );
  }

  return null;
}

export function assertValidPpdMatrix(matrix: unknown[][], parsed?: PpdParseResult): PpdParseResult {
  const result = parsed ?? parsePpdMatrix(matrix);
  const err = validatePpdMatrix(matrix, result);
  if (err) throw new Error(err);
  return result;
}

function parseMetadata(matrix: unknown[][]): PpdParsedMetadata {
  const extra: Record<string, string> = {};
  let buildName: string | undefined;
  let sppNumber: string | undefined;
  let catalogLabel: string | undefined;
  let catalogVersion: string | undefined;

  for (const row of matrix.slice(0, 30)) {
    const label = cellStr(row[0]).replace(/:$/, '').trim();
    const value = cellStr(row[2]) || cellStr(row[1]);
    if (!label) continue;

    const norm = label.toLowerCase();
    if (norm === 'název stavby') buildName = value || undefined;
    else if (norm.includes('spp') || norm.includes('číslo spp')) sppNumber = value || undefined;
    else if (norm === 'ceník') catalogLabel = value || undefined;
    else if (norm.includes('položková databáze')) catalogVersion = value || undefined;
    else if (label.endsWith(':') || norm.includes(':')) {
      extra[label.replace(/:$/, '')] = value;
    }
  }

  // Ceník může být v řádku 1 sloupci E/F
  const r1 = matrix[0] ?? [];
  if (!catalogLabel) {
    for (let c = 0; c < r1.length; c++) {
      if (cellStr(r1[c]).toLowerCase() === 'ceník:' && cellStr(r1[c + 1])) {
        catalogLabel = cellStr(r1[c + 1]);
        break;
      }
    }
  }

  return { buildName, sppNumber, catalogLabel, catalogVersion, extra };
}

function parsePpdLineRow(
  row: unknown[],
  qtyCol: number,
  totalCol: number,
  category: string | null,
  sortOrder: number,
): PpdParsedLine | null {
  let sap: string | null = null;
  let sapCol = 0;
  for (let c = 0; c <= 3; c++) {
    if (!isSapValue(row[c])) continue;
    sap = normalizeSap(row[c]);
    sapCol = c;
    break;
  }
  if (!sap) return null;

  const isConstruction = cellStr(row[sapCol + 1]).toUpperCase() === 'S';
  const name =
    cellStr(row[sapCol + 2]) || cellStr(row[sapCol + 3]) || cellStr(row[sapCol + 4]) || cellStr(row[2]) || cellStr(row[3]);
  const parsed = findQuantityInRow(row, qtyCol);
  if (!parsed) return null;

  return {
    sortOrder,
    category,
    sap,
    isConstruction,
    name,
    quantity: parsed.quantity,
    unit: parsed.unit,
    ppdLineTotal: findLineTotalInRow(row, totalCol),
  };
}

function isMaterialCategoryName(category: string | null): boolean {
  if (!category) return false;
  const n = normalizePpdHeaderText(category);
  return n.includes('material');
}

function rowHasSap(row: unknown[]): boolean {
  for (let c = 0; c <= 3; c++) {
    if (isSapValue(row[c])) return true;
  }
  return false;
}

function findLimitkaBoundary(matrix: unknown[][], sapHeaders: number[]): number {
  const { limitkaRow } = findPpdSectionBounds(matrix);
  if (limitkaRow >= 0) return limitkaRow;

  if (sapHeaders.length > 0) {
    return sapHeaders[sapHeaders.length - 1]!;
  }

  for (let i = 0; i < matrix.length; i++) {
    const n = normalizePpdHeaderText(cellStr(matrix[i]?.[0]));
    if (n.includes('material') && (n.includes('vyrazen') || n.includes('zhotovitele'))) return i;
  }
  return -1;
}

function findWorkSectionStart(matrix: unknown[][], workEnd: number, seznamRow: number): number {
  if (seznamRow >= 0) {
    const joined = normalizePpdHeaderText((matrix[seznamRow] ?? []).map(cellStr).join(' '));
    if (joined.includes('seznam') && joined.includes('poloz')) {
      return seznamRow + 1;
    }
  }

  for (let i = 0; i < workEnd; i++) {
    if (isPpdSapHeaderRow(matrix[i] ?? [])) return i + 1;
  }

  for (let i = 0; i < workEnd; i++) {
    if (rowHasSap(matrix[i] ?? [])) return i;
  }

  // Přeskočit rekapitulaci nákladů (PŘÍPRAVA s částkou v D, ne položky).
  const searchFrom = Math.min(40, workEnd);
  for (let i = searchFrom; i < workEnd; i++) {
    const n = normalizePpdHeaderText(cellStr(matrix[i]?.[0]));
    if (n.includes('priprava') && !rowHasSap(matrix[i] ?? [])) return i;
  }
  return -1;
}

function countNonMaterialLines(lines: PpdParsedLine[]): number {
  return lines.filter((l) => !isMaterialCategoryName(l.category)).length;
}

function appendWorkSectionIfMissing(
  matrix: unknown[][],
  sapHeaders: number[],
  seznamRow: number,
  lines: PpdParsedLine[],
): void {
  if (countNonMaterialLines(lines) >= 5) return;

  const workEnd = findLimitkaBoundary(matrix, sapHeaders);
  if (workEnd <= 0) return;

  const workStart = findWorkSectionStart(matrix, workEnd, seznamRow);
  if (workStart < 0 || workStart >= workEnd) return;

  const anchor = seznamRow >= 0 ? seznamRow : workStart - 1;
  if (hasSapHeaderBetween(sapHeaders, anchor, workEnd) && countNonMaterialLines(lines) > 0) return;

  const existingSaps = new Set(lines.map((l) => l.sap));
  const { qtyCol, totalCol } = inferWorkSectionColumns(matrix, workStart, workEnd);
  const sortOrderStart = lines.reduce((max, l) => Math.max(max, l.sortOrder), 0);
  const extra: PpdParsedLine[] = [];
  parsePpdDataRows(matrix, workStart, workEnd, qtyCol, totalCol, extra, sortOrderStart);
  for (const line of extra) {
    if (existingSaps.has(line.sap)) continue;
    lines.push(line);
  }
}

function findPpdSectionBounds(matrix: unknown[][]): { seznamRow: number; limitkaRow: number } {
  let seznamRow = -1;
  let limitkaRow = -1;
  for (let i = 0; i < matrix.length; i++) {
    if (!isPpdSectionTitleRow(matrix[i] ?? [])) continue;
    const joined = normalizePpdHeaderText((matrix[i] ?? []).map(cellStr).join(' '));
    if (joined.includes('seznam') && joined.includes('poloz')) seznamRow = i;
    if (joined.includes('limitka') && joined.includes('material')) limitkaRow = i;
  }
  return { seznamRow, limitkaRow };
}

function hasSapHeaderBetween(sapHeaders: number[], from: number, to: number): boolean {
  return sapHeaders.some((h) => h > from && h < to);
}

function parsePpdDataRows(
  matrix: unknown[][],
  startRow: number,
  endRow: number,
  qtyCol: number,
  totalCol: number,
  lines: PpdParsedLine[],
  sortOrderStart: number,
): number {
  let category: string | null = null;
  let sortOrder = sortOrderStart;

  for (let i = startRow; i < endRow; i++) {
    const row = matrix[i] ?? [];
    if (isPpdSectionTitleRow(row)) continue;
    if (isPpdSapHeaderRow(row)) continue;

    if (isCategoryRow(row)) {
      category = cellStr(row[0]);
      continue;
    }

    const line = parsePpdLineRow(row, qtyCol, totalCol, category, sortOrder + 1);
    if (!line) continue;
    sortOrder++;
    lines.push(line);
  }

  return sortOrder;
}

function parsePpdSection(
  matrix: unknown[][],
  headerRow: number,
  endRow: number,
  lines: PpdParsedLine[],
  sortOrderStart: number,
): number {
  const header = matrix[headerRow] ?? [];
  const qtyCol = findQuantityColumnIndex(header);
  const totalCol = findTotalPriceColumnIndex(header);
  return parsePpdDataRows(matrix, headerRow + 1, endRow, qtyCol, totalCol, lines, sortOrderStart);
}

function inferWorkSectionColumns(matrix: unknown[][], startRow: number, endRow: number): { qtyCol: number; totalCol: number } {
  for (let i = startRow; i < endRow; i++) {
    const row = matrix[i] ?? [];
    if (isPpdSapHeaderRow(row)) {
      return {
        qtyCol: findQuantityColumnIndex(row),
        totalCol: findTotalPriceColumnIndex(row),
      };
    }
  }
  return { qtyCol: 5, totalCol: 6 };
}

const REKAPITULACE_SAP_BASE = 900_000;

function isRekapitulaceStartRow(row: unknown[]): boolean {
  const joined = normalizePpdHeaderText((row ?? []).map(cellStr).join(' '));
  return joined.includes('rekapitulace') && joined.includes('naklad');
}

function isRekapitulaceEndRow(row: unknown[]): boolean {
  const joined = normalizePpdHeaderText((row ?? []).map(cellStr).join(' '));
  if (joined.includes('celkove') && joined.includes('naklad')) return true;
  if (joined.includes('seznam') && joined.includes('poloz')) return true;
  if (joined.includes('limitka') && joined.includes('material')) return true;
  return false;
}

/** Doplňkové řádky práce z rekapitulace — .xls export často neobsahuje buňky sekce Seznam položek. */
function parseRekapitulaceWorkLines(matrix: unknown[][]): PpdParsedLine[] {
  const lines: PpdParsedLine[] = [];
  let inBlock = false;
  let sortOrder = 0;

  for (const row of matrix) {
    if (isRekapitulaceStartRow(row)) {
      inBlock = true;
      continue;
    }
    if (!inBlock) continue;
    if (isRekapitulaceEndRow(row)) break;

    const category = cellStr(row[0]);
    if (!category || isSapValue(row[0]) || !isCategoryRow(row)) continue;
    if (isMaterialCategoryName(category)) continue;

    const amount =
      parsePpdMoneyText(row[3]) ??
      parsePpdMoneyText(row[4]) ??
      parsePpdMoneyText(row[2]);
    if (amount == null || amount <= 0) continue;

    sortOrder++;
    lines.push({
      sortOrder,
      category,
      sap: String(REKAPITULACE_SAP_BASE + sortOrder),
      isConstruction: false,
      name: `${category} (rekapitulace PPD)`,
      quantity: 1,
      unit: 'Kč',
      ppdLineTotal: amount,
    });
  }

  return lines;
}

function enrichIncompletePpdParse(matrix: unknown[][], result: PpdParseResult): PpdParseResult {
  // Rekapitulace je součet položek. Doplní se jen když v seznamu žádná práce není.
  if (countNonMaterialLines(result.lines) > 0) return result;

  const rekLines = parseRekapitulaceWorkLines(matrix);
  if (rekLines.length === 0) return result;

  const existingSaps = new Set(result.lines.map((l) => l.sap));
  let sortOrder = result.lines.reduce((max, l) => Math.max(max, l.sortOrder), 0);

  for (const line of rekLines) {
    if (existingSaps.has(line.sap)) continue;
    sortOrder++;
    result.lines.push({ ...line, sortOrder });
    existingSaps.add(line.sap);
  }

  result.metadata.extra['rekapitulaceWorkFallback'] = 'true';
  result.metadata.extra['rekapitulaceWorkLineCount'] = String(rekLines.length);
  return result;
}

/**
 * Parsuje matici buněk z prvního listu PPD XLSX.
 */
export function parsePpdMatrix(matrix: unknown[][]): PpdParseResult {
  const metadata = parseMetadata(matrix);
  const lines: PpdParsedLine[] = [];
  const sapHeaders = findAllPpdSapHeaderRows(matrix);
  const { seznamRow } = findPpdSectionBounds(matrix);

  if (sapHeaders.length === 0) {
    const sectionStart = findPpdItemsSectionStart(matrix);
    if (sectionStart >= 0) {
      const headerRow = matrix[sectionStart] ?? [];
      const hasHeader = isPpdSapHeaderRow(headerRow);
      const dataStart = hasHeader ? sectionStart + 1 : sectionStart;
      const qtyCol = hasHeader ? findQuantityColumnIndex(headerRow) : 5;
      const totalCol = hasHeader ? findTotalPriceColumnIndex(headerRow) : 6;
      parsePpdDataRows(matrix, dataStart, matrix.length, qtyCol, totalCol, lines, 0);
    }
  } else {
    let sortOrder = 0;
    for (let s = 0; s < sapHeaders.length; s++) {
      const headerRow = sapHeaders[s];
      const endRow = s + 1 < sapHeaders.length ? sapHeaders[s + 1] : matrix.length;
      sortOrder = parsePpdSection(matrix, headerRow, endRow, lines, sortOrder);
    }

    // .xls export: sloučené buňky — chybí hlavička „Číslo SAP“ u práce (někdy i titulek Seznam položek).
    appendWorkSectionIfMissing(matrix, sapHeaders, seznamRow, lines);
  }

  return enrichIncompletePpdParse(matrix, { metadata, lines });
}
